import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import type {
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridGroupingColDefOverride,
  GridRowGroupingModel,
} from "@mui/x-data-grid-premium";
import { GRID_CHECKBOX_SELECTION_COL_DEF, isAutogeneratedRow, useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import BadgeDetached from "@entrilia/shared/components/BadgeDetached";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import DataGridGroupHeader from "@entrilia/shared/components/grid/DataGridGroupHeader";
import { getRowSelectionModelFromIds, getSelectedRowIdsFromModel } from "@entrilia/shared/components/grid/gridHelper";
import { useGridGroupsExpanded } from "@entrilia/shared/components/grid/gridHooks";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { getAllNonGroupRowIds } from "@entrilia/shared/utilities/dataGridApiHelper";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../api/adminApi";
import { type ChecklistTask, type ObjectFieldValuesDetails } from "../../../api/types/objectTypes";
import useUserContext from "../../../context/useUserContext";
import ApprovalStatusTag from "../../approvals/ApprovalStatusTag";
import { getCheckedGridSx } from "../../common/grid/gridStyles";
import UsersAvatarGroup from "../../common/UsersAvatarGroup";
import UserWithTimestamp from "../../common/UserWithTimestamp";
import PanelSection from "../../common/layout/PanelSection";
import PanelSectionHeader from "../../common/layout/PanelSectionHeader";
import ChecklistTaskStatusTag from "./ChecklistTaskStatusTag";
import ObjectTaskDetailsDrawer from "./ObjectTaskDetailsDrawer";
import ObjectTasksActions from "./ObjectTasksActions";
import { ObjectTasksContextProvider } from "./ObjectTasksContext";
import ObjectTasksGridToolbar from "./ObjectTasksGridToolbar";
import { getEditableChecklistTaskFields, isTaskApprovalAllowed } from "./objectTasksHelper";

interface Props {
  objectData: ObjectFieldValuesDetails;
  hasPermissionsToManageObjectData: boolean;
  onEntityUpdate: (newObjectData: ObjectFieldValuesDetails) => void;
  onApprovalActionExecuted: () => void;
}

const noCategoryLabel = "No Category";

const gridRowGroupingModel: GridRowGroupingModel = ["category"];

const gridColumnVisibilityModel: GridColumnVisibilityModel = {
  category: false,
  name: false,
};

const groupingColumnDefinition: GridGroupingColDefOverride<ChecklistTask> = {
  leafField: "name",
  renderHeader: (params) => <DataGridGroupHeader params={params} />,
  minWidth: 160,
  flex: 2,
  colSpan: (value, row, _, apiRef) =>
    value && isAutogeneratedRow(row) ? apiRef.current.getVisibleColumns().length : 1,
};

const columnDefinitions: GridColDef<ChecklistTask>[] = [
  {
    field: nameof<ChecklistTask>("category"),
    renderCell: ({ rowNode }) => {
      if (rowNode.type !== "group") {
        return null;
      }

      return <Typography variant="subtitle2">{rowNode.groupingKey?.toString() ?? noCategoryLabel}</Typography>;
    },
  },
  {
    field: nameof<ChecklistTask>("name"),
    headerName: "Name",
    flex: 2,
    minWidth: 160,
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return null;
      }

      return (
        <TypographyTooltipEllipsis
          text={row.name}
          typographyProps={{ className: "highlighted-action", sx: { pl: 6 } }}
        />
      );
    },
  },
  {
    field: nameof<ChecklistTask>("status"),
    headerName: "Status",
    flex: 0.75,
    minWidth: 128,
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return null;
      }

      return <ChecklistTaskStatusTag status={row.status} />;
    },
  },
  {
    field: nameof<ChecklistTask>("approvers"),
    headerName: "Approvers",
    flex: 0.75,
    minWidth: 96,
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return null;
      }

      return <UsersAvatarGroup users={row.approvers} />;
    },
  },
  {
    field: nameof<ChecklistTask>("completedBy"),
    headerName: "Prepared",
    flex: 1,
    minWidth: 192,
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return null;
      }

      if (!row.completedBy?.name) {
        return "";
      }

      return <UserWithTimestamp userName={row.completedBy.name} timestamp={row.completedAt} />;
    },
  },
  {
    field: nameof<ChecklistTask>("approvalStatus"),
    headerName: "Approval",
    flex: 0.75,
    minWidth: 128,
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group" || !row.approvalStatus) {
        return null;
      }

      return <ApprovalStatusTag status={row.approvalStatus} />;
    },
  },
  {
    field: nameof<ChecklistTask>("approvedBy"),
    headerName: "Reviewed",
    flex: 1,
    minWidth: 192,
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return null;
      }

      if (!row.approvedBy?.name) {
        return "";
      }

      return <UserWithTimestamp userName={row.approvedBy.name} timestamp={row.approvedAt} />;
    },
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 160,
    align: "right",
    headerAlign: "right",
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return null;
      }

      return <ObjectTasksActions row={row} />;
    },
  },
];

type FilterType = "all" | "pending_my_approval";

const ObjectTasksSection = ({
  objectData,
  hasPermissionsToManageObjectData,
  onEntityUpdate,
  onApprovalActionExecuted,
}: Props) => {
  const apiRef = useGridApiRef();
  const { id: userId } = useUserContext();

  const getChecklistTaskTemplateDefinition = useCallback(
    () => adminApi.getObjectDefinition("ChecklistTaskTemplate"),
    []
  );

  const [checklistTaskTemplateDefinition, fetchDefinitionError] = useFetch(getChecklistTaskTemplateDefinition);

  const [isSavingTaskId, setIsSavingTaskId] = useState<string>();
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<string>();
  const [filter, setFilter] = useState<FilterType>("all");

  const { isGroupExpanded } = useGridGroupsExpanded(apiRef);

  const showToolbar = useMemo(() => selectedRowIds.length > 0, [selectedRowIds.length]);

  const selectedTask = useMemo(
    () => objectData.checklistTasks.find((t) => t.id === selectedTaskId),
    [objectData.checklistTasks, selectedTaskId]
  );

  const handleChecklistTasksUpdate = useCallback(
    (updatedTasks: ChecklistTask[]) => {
      const updatedTasksMap = new Map(updatedTasks.map((t) => [t.id, t]));
      onEntityUpdate({
        ...objectData,
        checklistTasks: objectData.checklistTasks.map((task) => updatedTasksMap.get(task.id) ?? task),
      });
    },
    [objectData, onEntityUpdate]
  );

  const pendingMyApprovalTasks = useMemo(
    () => objectData.checklistTasks.filter((t) => isTaskApprovalAllowed(t, userId)),
    [objectData.checklistTasks, userId]
  );

  const visibleTasks = useMemo(
    () => (filter === "all" ? objectData.checklistTasks : pendingMyApprovalTasks),
    [filter, objectData.checklistTasks, pendingMyApprovalTasks]
  );

  const hasCheckboxSelection = useMemo(
    () => hasPermissionsToManageObjectData || pendingMyApprovalTasks.length > 0,
    [hasPermissionsToManageObjectData, pendingMyApprovalTasks.length]
  );

  const handleCellClick = useCallback(({ field, row, rowNode }: GridCellParams<ChecklistTask>) => {
    if (rowNode.type === "group" || field === "actions" || field === GRID_CHECKBOX_SELECTION_COL_DEF.field) {
      return;
    }

    setSelectedTaskId(row.id);
  }, []);

  const handleFilterChange = (newFilter: FilterType) => {
    setFilter(newFilter);
    setSelectedRowIds([]);
    setSelectedTaskId(undefined);
  };

  const handleNavigate = (direction: 1 | -1) => {
    if (!selectedTaskId) {
      return;
    }

    const taskRowIds = getAllNonGroupRowIds(apiRef);
    if (!taskRowIds) {
      return;
    }

    const selectedTaskIndex = taskRowIds.indexOf(selectedTaskId);
    if (selectedTaskIndex === -1) {
      return;
    }

    const nextTaskIndex = (taskRowIds.length + selectedTaskIndex + direction) % taskRowIds.length;
    const nextTaskId = taskRowIds[nextTaskIndex];
    if (nextTaskId) {
      setSelectedTaskId(nextTaskId.toString());
    }
  };

  if (fetchDefinitionError) {
    logError(fetchDefinitionError, "[ObjectTasksSection] getChecklistTaskTemplateDefinition");
    return <DataLoadingFailed title="Failed to load checklist task fields" />;
  }

  return (
    <PanelSection isLoading={checklistTaskTemplateDefinition === undefined}>
      <PanelSectionHeader title="Tasks">
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_e, newFilter: FilterType | null) => {
            if (newFilter) {
              handleFilterChange(newFilter);
            }
          }}
        >
          <ToggleButton value="all" sx={{ gap: 0.5 }}>
            All <BadgeDetached showZero badgeContent={objectData.checklistTasks.length} color="secondary" />
          </ToggleButton>
          <ToggleButton value="pending_my_approval" sx={{ gap: 0.5 }}>
            Pending my approval{" "}
            <BadgeDetached showZero badgeContent={pendingMyApprovalTasks.length} color="secondary" />
          </ToggleButton>
        </ToggleButtonGroup>
      </PanelSectionHeader>
      {checklistTaskTemplateDefinition !== undefined && (
        <ObjectTasksContextProvider
          objectType={objectData.objectType}
          objectId={objectData.id}
          hasPermissionsToManageObjectData={hasPermissionsToManageObjectData}
          editableChecklistTaskFields={getEditableChecklistTaskFields(
            checklistTaskTemplateDefinition.objectClassDefinition.fields
          )}
          onChecklistTasksUpdate={handleChecklistTasksUpdate}
          onApprovalActionExecuted={onApprovalActionExecuted}
          isSavingTaskId={isSavingTaskId}
          setSavingTaskId={setIsSavingTaskId}
        >
          <Box sx={{ px: 2, pb: 2 }}>
            <DataGridHeaderToolbarProvider
              showToolbar={showToolbar}
              selection={[selectedRowIds, setSelectedRowIds]}
              actions={
                <ObjectTasksGridToolbar
                  allTasks={visibleTasks}
                  selectedIds={selectedRowIds}
                  setSelectedIds={setSelectedRowIds}
                />
              }
            >
              <DataGrid<ChecklistTask>
                apiRef={apiRef}
                rows={visibleTasks}
                columns={columnDefinitions}
                groupingColDef={groupingColumnDefinition}
                rowGroupingModel={gridRowGroupingModel}
                columnVisibilityModel={gridColumnVisibilityModel}
                isGroupExpandedByDefault={isGroupExpanded}
                noRowsText="No tasks"
                multilineRows
                checkboxSelection={hasCheckboxSelection}
                selectableRows
                rowSelectionModel={getRowSelectionModelFromIds(selectedRowIds)}
                onRowSelectionModelChange={(selection) => setSelectedRowIds(getSelectedRowIdsFromModel(selection))}
                onCellClick={handleCellClick}
                sx={hasCheckboxSelection ? getCheckedGridSx : undefined}
                slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
                disableColumnPinning
                disableColumnReorder
                disableColumnSorting
                noScrollableContainer
              />
            </DataGridHeaderToolbarProvider>
          </Box>

          {selectedTask && (
            <ObjectTaskDetailsDrawer
              task={selectedTask}
              navigationDisabled={visibleTasks.length < 2}
              onNavigate={handleNavigate}
              onClose={() => setSelectedTaskId(undefined)}
            />
          )}
        </ObjectTasksContextProvider>
      )}
    </PanelSection>
  );
};

export default ObjectTasksSection;
